// src/admincomponents/UserTable.js
import React, { useState } from 'react';
import styled from 'styled-components';
import { apiClient } from '../../api/apiClient';
import {
    AdminButton, ButtonRow, SubTitle, TableWrap, Table, Th, Td, Tr,
    ModalOverlay, ModalPanel, ModalTitle, ModalFooter, Form, FormGroup, FormRow,
    Label, TextInput, Select, TextArea, Fieldset, CheckLabel,
} from './adminStyles';

const SectionBlock = styled.div`
    margin-bottom: 36px;
    &:last-child { margin-bottom: 0; }
`;

const Toolbar = styled.div`
    display: flex;
    justify-content: flex-end;
    margin-bottom: 20px;
`;

const CheckGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px 16px;
    margin: 4px 0 10px;
    @media (max-width: 520px) { grid-template-columns: 1fr; }
`;

const AddUserModal = ({ onClose, onUserAdded }) => {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [email, setEmail] = useState('');
    const [role, setRole] = useState('user');
 
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!username || !password || !email) {
            alert('请填写所有必填项');
            return;
        }
        try {
            const response = await apiClient.post('/admin/users', { username, password, email, role });
            onUserAdded(response.data);
            alert('用户创建成功!');
            onClose();
        } catch (err) {
            alert('错误: ' + (err.response?.data?.error || '创建用户失败'));
        }
    };
 
    return (
        <ModalOverlay onClick={onClose}>
            <ModalPanel role="dialog" aria-modal="true" aria-labelledby="add-user-title" onClick={e => e.stopPropagation()}>
                <ModalTitle id="add-user-title">新增用户</ModalTitle>
                <Form onSubmit={handleSubmit}>
                    <FormGroup>
                        <Label htmlFor="add-user-username">用户名</Label>
                        <TextInput id="add-user-username" type="text" value={username} onChange={e => setUsername(e.target.value)} required />
                    </FormGroup>
                    <FormGroup>
                        <Label htmlFor="add-user-password">密码</Label>
                        <TextInput id="add-user-password" type="password" value={password} onChange={e => setPassword(e.target.value)} required />
                    </FormGroup>
                    <FormGroup>
                        <Label htmlFor="add-user-email">邮箱</Label>
                        <TextInput id="add-user-email" type="email" value={email} onChange={e => setEmail(e.target.value)} required />
                    </FormGroup>
                    <FormGroup>
                        <Label htmlFor="add-user-role">角色</Label>
                        <Select id="add-user-role" value={role} onChange={e => setRole(e.target.value)}>
                            <option value="user">普通用户</option>
                            <option value="admin">管理员</option>
                        </Select>
                    </FormGroup>
                    <ModalFooter>
                        <AdminButton type="button" onClick={onClose}>取消</AdminButton>
                        <AdminButton type="submit" $variant="primary">创建用户</AdminButton>
                    </ModalFooter>
                </Form>
            </ModalPanel>
        </ModalOverlay>
    );
};

// --- EditUserModal 组件 
const EditUserModal = ({ user: targetUser, allPermissions, currentUser, onClose, onUserUpdated }) => {
    // 核心信息状态
    const [role, setRole] = useState(targetUser.role);
    const [rank, setRank] = useState(targetUser.rank);
    const [permissions, setPermissions] = useState({ ...targetUser.permissions });
 
    // 新增: 档案信息状态
    const [nickname, setNickname] = useState(targetUser.nickname || '');
    const [level, setLevel] = useState(targetUser.level || 0);
    const [signature, setSignature] = useState(targetUser.signature || '');
    const [isVerified, setIsVerified] = useState(targetUser.is_verified || false);
 
    const handlePermissionChange = (e) => {
        const { name, checked } = e.target;
        setPermissions(prev => ({ ...prev, [name]: checked }));
    };
 
    const handleRoleChange = (e) => {
        setRole(e.target.value);
        if (e.target.value === 'user') {
            setPermissions({});
        }
    };
 
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (rank === '' || rank <= currentUser.rank) {
            alert(`Rank 必须是大于您当前等级 (${currentUser.rank}) 的数字。`);
            return;
        }
        try {
            const payload = {
                role, rank, permissions,
                nickname, level, signature, is_verified: isVerified
            };
            const response = await apiClient.put(`/admin/users/${targetUser.id}`, payload);
            onUserUpdated(response.data);
            alert('用户信息更新成功!');
            onClose();
        } catch (err) {
            alert('错误: ' + (err.response?.data?.error || '更新用户失败'));
        }
    };
    
    const isTargetSiteOwner = targetUser.role === 'site_owner';
 
    return (
        <ModalOverlay onClick={onClose}>
            <ModalPanel $wide role="dialog" aria-modal="true" aria-labelledby="edit-user-title" onClick={e => e.stopPropagation()}>
                <ModalTitle id="edit-user-title">编辑用户 · {targetUser.username}</ModalTitle>
                <Form onSubmit={handleSubmit}>
                    <Fieldset>
                        <legend>核心信息</legend>
                        <FormRow>
                            <FormGroup style={{ marginBottom: 12 }}>
                                <Label htmlFor="edit-user-role">角色</Label>
                                <Select id="edit-user-role" value={role} onChange={handleRoleChange} disabled={isTargetSiteOwner}>
                                    <option value="user">普通用户</option>
                                    <option value="admin">管理员</option>
                                </Select>
                            </FormGroup>
                            <FormGroup style={{ marginBottom: 12 }}>
                                <Label htmlFor="edit-user-rank">Rank<small>你的等级 {currentUser.rank}</small></Label>
                                <TextInput id="edit-user-rank" type="number" value={rank} onChange={e => setRank(parseInt(e.target.value, 10))} min={currentUser.rank + 1} disabled={isTargetSiteOwner}/>
                            </FormGroup>
                        </FormRow>
                    </Fieldset>

                    <Fieldset>
                        <legend>个人档案</legend>
                        <FormRow>
                            <FormGroup style={{ marginBottom: 12 }}>
                                <Label htmlFor="edit-user-nickname">昵称</Label>
                                <TextInput id="edit-user-nickname" type="text" value={nickname} onChange={e => setNickname(e.target.value)} disabled={isTargetSiteOwner} />
                            </FormGroup>
                            <FormGroup style={{ marginBottom: 12 }}>
                                <Label htmlFor="edit-user-level">等级</Label>
                                <TextInput id="edit-user-level" type="number" value={level} onChange={e => setLevel(parseInt(e.target.value, 10))} disabled={isTargetSiteOwner} />
                            </FormGroup>
                        </FormRow>
                        <FormGroup style={{ marginBottom: 12 }}>
                            <Label htmlFor="edit-user-signature">个性签名</Label>
                            <TextArea id="edit-user-signature" value={signature} onChange={e => setSignature(e.target.value)} style={{ minHeight: 80 }} disabled={isTargetSiteOwner} />
                        </FormGroup>
                        <FormGroup style={{ marginBottom: 12 }}>
                            <CheckLabel $disabled={isTargetSiteOwner}>
                                <input type="checkbox" checked={isVerified} onChange={e => setIsVerified(e.target.checked)} disabled={isTargetSiteOwner} /> 邮箱已验证
                            </CheckLabel>
                        </FormGroup>
                    </Fieldset>

                    {role === 'admin' && (
                        <Fieldset>
                            <legend>权限管理</legend>
                            <CheckGrid>
                                {Object.entries(allPermissions).map(([key, permissionName]) => {
                                    const canGrant = currentUser.role === 'site_owner' || (currentUser.permissions && currentUser.permissions[permissionName]);
                                    return (
                                        <CheckLabel key={key} $disabled={!canGrant}>
                                            <input type="checkbox" name={permissionName} checked={!!permissions[permissionName]} onChange={handlePermissionChange} disabled={!canGrant} />
                                            {permissionName}
                                        </CheckLabel>
                                    );
                                })}
                            </CheckGrid>
                        </Fieldset>
                    )}

                    <ModalFooter>
                        <AdminButton type="button" onClick={onClose}>取消</AdminButton>
                        <AdminButton type="submit" $variant="primary" disabled={isTargetSiteOwner}>保存修改</AdminButton>
                    </ModalFooter>
                </Form>
            </ModalPanel>
        </ModalOverlay>
    );
};
 
// --- 主组件: UserTable ---
function UserTable({ users, currentUser, allPermissions, onUserAdded, onUserUpdated, onUserDeleted }) {
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [isAddModalOpen, setIsAddModalOpen] = useState(false); // 新增状态
 
    const admins = users.filter(u => u.role === 'admin' || u.role === 'site_owner');
    const regularUsers = users.filter(u => u.role === 'user');
 
    const handleOpenEditModal = (user) => {
        setEditingUser(user);
        setIsEditModalOpen(true);
    };
 
    const handleDelete = async (user) => {
        if (window.confirm(`确认删除用户 "${user.username}" 吗?`)) {
            try {
                await apiClient.delete(`/admin/users/${user.id}`);
                onUserDeleted(user.id);
                alert('用户删除成功!');
            } catch (err) {
                alert('错误: ' + (err.response?.data?.error || '删除用户失败'));
            }
        }
    };
 
    const renderTable = (userList, title) => (
        <SectionBlock>
            <SubTitle>{title}</SubTitle>
            <TableWrap>
                <Table>
                    <thead>
                        <tr>
                            <Th>ID</Th>
                            <Th>用户名</Th>
                            <Th>邮箱</Th>
                            <Th>昵称</Th>
                            <Th>等级</Th>
                            <Th>Rank</Th>
                            <Th>角色</Th>
                            <Th>操作</Th>
                        </tr>
                    </thead>
                    <tbody>
                        {userList.map(user => {
                            const canManageTargetUser = currentUser.rank < user.rank;
                            const isSelf = user.id === currentUser.id;
                            return (
                                <Tr key={user.id}>
                                    <Td $mono>{user.id}</Td>
                                    <Td>{user.username}</Td>
                                    <Td $dim>{user.email}</Td>
                                    <Td $dim>{user.nickname || '-'}</Td>
                                    <Td $mono>{user.level}</Td>
                                    <Td $mono>{user.rank}</Td>
                                    <Td $mono>{user.role}</Td>
                                    <Td>
                                        {canManageTargetUser && (
                                            <ButtonRow>
                                                <AdminButton type="button" $small onClick={() => handleOpenEditModal(user)}>编辑</AdminButton>
                                                {user.role !== 'site_owner' && !isSelf && (
                                                    <AdminButton type="button" $small $variant="danger" onClick={() => handleDelete(user)}>删除</AdminButton>
                                                )}
                                            </ButtonRow>
                                        )}
                                    </Td>
                                </Tr>
                            );
                        })}
                    </tbody>
                </Table>
            </TableWrap>
        </SectionBlock>
    );

    return (
        <div>
            <Toolbar>
                <AdminButton type="button" $variant="primary" onClick={() => setIsAddModalOpen(true)}>+ 新增用户</AdminButton>
            </Toolbar>
            {renderTable(admins, "管理员与站长")}
            {renderTable(regularUsers, "普通用户")}

            {isAddModalOpen && <AddUserModal onClose={() => setIsAddModalOpen(false)} onUserAdded={onUserAdded} />}

            {isEditModalOpen && (
                <EditUserModal
                    user={editingUser}
                    allPermissions={allPermissions}
                    currentUser={currentUser}
                    onClose={() => setIsEditModalOpen(false)}
                    onUserUpdated={onUserUpdated}
                />
            )}
        </div>
    );
}


export default UserTable;
